import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../../config';
import {
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    MapPinIcon,
    UsersIcon,
    ShoppingBagIcon
} from '@heroicons/react/24/outline';

const AdminCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('leads'); // leads, customers

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCustomers(response.data);
        } catch (error) {
            console.error('Failed to fetch customers:', error);
            toast.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const getRoleBadge = (role) => {
        if (role === 'admin') {
            return <span className="inline-flex px-3 py-1 text-xs font-bold rounded-full bg-purple-100 text-purple-800">Admin</span>;
        }
        return <span className="inline-flex px-3 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-800">Customer</span>;
    };

    const TabButton = ({ id, label, icon: Icon }) => (
        <button
            onClick={() => setActiveSection(id)}
            className={`flex items-center px-6 py-3 text-sm font-medium transition-all border-b-2 ${activeSection === id
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
        >
            <Icon className="h-4 w-4 mr-2" />
            {label}
        </button>
    );

    const CustomerTable = ({ users, type }) => {
        if (users.length === 0) {
            return (
                <div className="text-center py-12">
                    <UserIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No {type.toLowerCase()} found</p>
                </div>
            );
        }

        return (
            <div className="overflow-hidden rounded-2xl border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Contact</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Address</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Joined</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {users.map((customer) => (
                            <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                            {customer.name ? customer.name.charAt(0).toUpperCase() : 'U'}
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">{customer.name || 'N/A'}</div>
                                            <div className="text-sm text-gray-500">ID: {customer.id.slice(-8)}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex flex-col space-y-1">
                                        <div className="flex items-center text-sm text-gray-900">
                                            <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                                            {customer.email}
                                        </div>
                                        <div className="flex items-center text-sm text-gray-500">
                                            <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                                            {customer.phone || 'N/A'}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-start text-sm text-gray-500 max-w-xs">
                                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5 shrink-0" />
                                        <span className="truncate" title={customer.address}>
                                            {customer.address || 'N/A'}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getRoleBadge(customer.role)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-gray-500">
                                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                                        {new Date(customer.created_at).toLocaleDateString()}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    if (loading) {
        return <div className="py-12 text-center text-gray-500 text-lg">Loading customers...</div>;
    }

    const leads = customers.filter(c => !c.has_ordered);
    const payingCustomers = customers.filter(c => c.has_ordered);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm overflow-hidden"
        >
            <div className="p-4 lg:p-8 pb-0">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Customer Management</h2>
                        <p className="text-gray-500 text-sm mt-1">Manage leads and paying customers</p>
                    </div>
                </div>

                <div className="flex border-b border-gray-100 mb-8 overflow-x-auto custom-scrollbar">
                    <div className="flex min-w-full">
                        <TabButton id="leads" label={`Leads (${leads.length})`} icon={UsersIcon} />
                        <TabButton id="customers" label={`Customers (${payingCustomers.length})`} icon={ShoppingBagIcon} />
                    </div>
                </div>
            </div>

            <div className="p-4 lg:p-8 pt-0">
                <AnimatePresence mode="wait">
                    {activeSection === 'leads' && (
                        <motion.div
                            key="leads"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <CustomerTable users={leads} type="Leads" />
                        </motion.div>
                    )}

                    {activeSection === 'customers' && (
                        <motion.div
                            key="customers"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <CustomerTable users={payingCustomers} type="Customers" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default AdminCustomers;
